import React, { useState } from 'react';

import { ENV } from '@environment';
import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import Field from 'components/Field';
import TextArea from 'components/TextArea';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import ResponseScreen, { BackgroundTypes, IconTypes } from 'screens/components/ResponseScreen';
import { sendSupportEmail } from 'services/registrationService';
import { htmlT } from 'utils/generic';

import { Input } from '../../../styles';
import { EmailValues } from '../../Email';
import { Container, Header, StyledForm, ButtonContainer, SendButton } from './styles';

interface Props {
  email: EmailValues | null;
}

interface Values {
  name: string;
  message: string;
}

const LANDING_URL = ENV.LANDING_PAGE as string;

const HelpMail = ({ email }: Props) => {
  const { t } = useStoreTranslation();
  const isDesktop = useIsDesktop();
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  if (isDesktop) return null;

  const initialValues: Values = {
    name: '',
    message: '',
  };

  const validationSchema = yup.object().shape({
    name: yup.string().required(),
    message: yup.string().required(),
  });

  const handleSubmit = async (values: Values) => {
    if (!email) return;

    setLoading(true);
    await sendSupportEmail({
      email: email.email.trim(),
      subject: t('registrationOnboarding.help.mail.emailSubject'),
      ...values,
    });
    setSuccess(true);
    setLoading(false);
  };

  const handleGoHome = () => {
    window.location.href = LANDING_URL;
  };

  if (!email) return null;

  return (
    <>
      <Container>
        <Header>{htmlT(t, 'registrationOnboarding.help.mail.title')}</Header>

        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
        >
          {({ isValid }: FormikValues) => (
            <StyledForm>
              <div>
                <Field name="name">
                  <Input
                    placeholder={t('registrationOnboarding.help.mail.namePlaceholder')}
                    autoCapitalize="off"
                    autoCorrect="off"
                  />
                </Field>

                <Field name="message" label={t('registrationOnboarding.help.mail.messageLabel')}>
                  <TextArea placeholder={t('registrationOnboarding.help.mail.messagePlaceholder')} />
                </Field>

                <ButtonContainer>
                  <SendButton disabled={!isValid || loading} loading={loading}>
                    {t('registrationOnboarding.help.mail.send')}
                  </SendButton>
                </ButtonContainer>
              </div>
            </StyledForm>
          )}
        </Formik>
      </Container>

      <ResponseScreen
        show={success}
        text={t('registrationOnboarding.help.mail.success.title')}
        subText={t('registrationOnboarding.help.mail.success.message', { mail: email.email })}
        buttonText={t('registrationOnboarding.help.mail.success.continue')}
        buttonOnClick={handleGoHome}
        background={BackgroundTypes.Cloud}
        icon={IconTypes.PaperAirplane}
      />
    </>
  );
};

export default HelpMail;
